import { type Country, dateFormats, useCountries, useFormatAmount, useIntl } from '@capsicum/common';
import { type BidderRowProps } from '@capsicum/features/fanshop/shared';
import styled from 'styled-components';

import { ShippingFlag } from '../shipping-flag/ShippingFlag';

export const BidderRow = ({
	amount,
	amountChz,
	chzCurrencyShortCode,
	date,
	fiatCurrencyShortCode,
	iso2Country,
	isOwnBid,
	mwsUserId
}: BidderRowProps) => {
	const { getCountryBy, getCountryFlagUri } = useCountries();
	const { $t } = useIntl();
	const { formatAmount } = useFormatAmount();
	const country = getCountryBy(iso2Country?.toUpperCase(), 'iso2') as Country;

	const bidderName = (
		<S.StyledTextBold $isOwnBid={isOwnBid}>
			{isOwnBid
				? $t('rewards.auctions.bids.bidderRow.you').toUpperCase()
				: `${$t('rewards.auctions.bids.bidderRow.user')} ${mwsUserId}`}
		</S.StyledTextBold>
	);

	const formattedDate = dateFormats.format(date, 'DD MMMM YYYY HH:mm:ss');

	return (
		<S.Layout $isOwnBid={isOwnBid}>
			<ShippingFlag url={getCountryFlagUri(country)} />
			<S.TextContainer>
				<div>
					{bidderName}
					<S.StyledText>{formattedDate}</S.StyledText>
				</div>
				<div>
					<S.StyledAmount>{`${formatAmount(amount)} ${fiatCurrencyShortCode}`}</S.StyledAmount>
					<S.StyledChzAmount>
						{`(${formatAmount(amountChz, {
							maximumFractionDigits: 2,
							minimumFractionDigits: 2
						})} ${chzCurrencyShortCode})`}
					</S.StyledChzAmount>
				</div>
			</S.TextContainer>
		</S.Layout>
	);
};

namespace S {
	export const Layout = styled.div<{ $isOwnBid: boolean }>`
		display: flex;
		padding: ${({ theme }) => theme.spacings.xxxsmall};
		${({ theme }) => theme.border.radius.medium};
		background-color: ${({ $isOwnBid, theme }) =>
			$isOwnBid ? theme.colors.backgrounds.info.subtle : theme.colors.backgrounds.main.container};
		flex-direction: row;
		align-items: center;
	`;

	export const TextContainer = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
		flex: 1;
		margin-left: ${({ theme }) => theme.spacings.small};
	`;

	export const StyledTextBold = styled.div<{ $isOwnBid: boolean }>`
		${({ theme }) => theme.fonts.text.text4};
		color: ${({ $isOwnBid, theme }) =>
			$isOwnBid ? theme.colors.text.brand.enabled : theme.colors.text.main.enabled};
	`;

	export const StyledText = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledAmount = styled.div`
		${({ theme }) => theme.fonts.text.text4};
		text-align: right;
		color: ${({ theme }) => theme.colors.text.main.enabled};
	`;

	export const StyledChzAmount = styled.div`
		${({ theme }) => theme.fonts.text.caption2};
		text-align: right;
		color: ${({ theme }) => theme.colors.text.main.subtle};
	`;
}
